import { Local697FormOfPayment } from "app/calcs/customers/local697/interfaces"
import { Local697PlanPPensionCalculations } from "app/calcs/customers/local697PlanP"
import { SimplePensionFormOfPayment } from "app/calcs/pensions/interfaces"
import { RetirementAgeSelector } from "app/components/form/RetirementAgeSelector"
import {
    IProfileContainerState,
    PensionQuestionsGroupContainer,
} from "app/containers/PipelineSection/Pension/Container"
import { IQuestionsGroupContainerProps } from "app/containers/PipelineSection/QuestionGroupContainer"
import { cachedLocal697ExportExhibitTab } from "app/redux/Customers/local697/actions"
import { dateAtAge, IRetirementPerson } from "app/redux/Profile/model"
import * as utils from "app/utils"
import { ILocal697PlanPInfo } from "app/workbookDAOs/local697DAO"
import * as Config from "config"
import { ColumnNames } from "config/697/ETL/interfaces"
import * as _ from "lodash"
import * as moment from "moment"
import * as React from "react"
import { Col, FormGroup, Row } from "react-bootstrap"

enum ExhibitGenerationState {
    idle,
    generating,
    ready,
}

interface ILocal697PensionContainerState extends IProfileContainerState {
    headOfHousehold: IRetirementPerson
    monthlyPayout: number
    info: ILocal697PlanPInfo
    exhibitGeneration: ExhibitGenerationState
    downloadFilename: string

    // these are synced with profile.pension.local697
    planPPaymentsAge: moment.Duration
    formOfPayment: Local697FormOfPayment
    hoursWorked: number
    payRate: number
}

class Local697PlanPPensionContainer extends PensionQuestionsGroupContainer<
    IQuestionsGroupContainerProps,
    ILocal697PensionContainerState
> {
    planPPaymentsAgeNS = "pension.local697.planPPaymentsAge"
    hoursWorkedNS = "pension.shared.hoursWorked"
    payRateNS = "pension.local697.payRate"
    formOfPaymentNS = "pension.local697.formOfPayment"

    state: ILocal697PensionContainerState = {
        profile: null,
        info: null,
        headOfHousehold: null,
        formOfPayment:
            this.getValueForQuestion(this.formOfPaymentNS) ||
            SimplePensionFormOfPayment.singleLifeOnly,
        payRate: this.getValueForQuestion(this.payRateNS) || 0,
        hoursWorked: this.getValueForQuestion(this.hoursWorkedNS) || 0,
        monthlyPayout: 0,
        planPPaymentsAge:
            this.getValueForQuestion(this.planPPaymentsAgeNS) || null,
        exhibitGeneration: ExhibitGenerationState.idle,
        downloadFilename: null,
    }

    initCalc = false

    debounceCalculations = _.debounce(() => {
        // tslint:disable-next-line:no-console
        //console.log('local697.debounceCalculations entered')
        const person = this.state.headOfHousehold
        const profile = this.state.profile
        const inputs = this.state.profile.pension.local697
        const { planPPaymentsAge } = this.state
        const when = dateAtAge(person, planPPaymentsAge)

        const computer = new Local697PlanPPensionCalculations(
            this.state.profile
        )
        computer.setSpouse(profile.getSpouse())
        computer
            .getMonthlyPensionAmount(person, inputs, when)
            .then((payout) => {
                // tslint:disable-next-line:no-debugger
                //debugger
                const info = computer.info
                const monthlyPayout = payout.amount
                this.setState({ monthlyPayout, info })
            })
    }, 300)

    componentWillMount() {
        this.onAppStateUpdate((appState) => {
            const profile = appState.Profile.getNative("info")
            const headOfHousehold = profile.getHeadOfHousehold()

            if (profile.pension.local697) {
                this.debounceCalculations()
            }

            this.setState({ headOfHousehold, profile })
        })
    }

    componentWillUpdate(
        nextProps: IQuestionsGroupContainerProps,
        nextState: ILocal697PensionContainerState
    ) {
        this.updateCalculations(nextState)
    }

    isPlanPEligible() {
        const e: string =
            this.state.profile.extraInfo[ColumnNames.planPEligible] || "n"
        return e.toLowerCase() === "y"
    }

    onExportExhibits() {
        this.setState({ exhibitGeneration: ExhibitGenerationState.generating })
        const profile = this.state.profile
        const member = profile.getHeadOfHousehold()
        const spouse = profile.getSpouse()
        const shared = profile.pension.shared
        const vals = profile.pension.local697
        const ioInputs = profile.pension.IO
        const nebfInputs = profile.pension.NEBF
        cachedLocal697ExportExhibitTab({
            member,
            spouse,
            vals,
            ioInputs,
            nebfInputs,
            shared,
            when: null,
        })
            // NOTE: I don't know why this comes back as a workbook response
            // that has 'message' at the wrong level....
            .then((xx: any) => {
                this.setState({
                    exhibitGeneration: ExhibitGenerationState.ready,
                })
                // tslint:disable-next-line:no-debugger
                debugger
                const endpoint = Config.getWebServicesConfig().miscEndpoint
                const downloadFilename =
                    endpoint +
                    "api/misc/downloadTransactionFile?path=" +
                    xx.Message
                this.setState({ downloadFilename })
            })
    }

    updateCalculations(nextState: ILocal697PensionContainerState) {
        const {
            profile,
            formOfPayment,
            headOfHousehold,
            hoursWorked,
            payRate,
            planPPaymentsAge,
        } = nextState

        if (!profile) {
            return
        }

        profile.pension = profile.pension || {}
        nextState.planPPaymentsAge =
            nextState.planPPaymentsAge ||
            moment.duration(headOfHousehold.selectedRetirementAge, "years")

        const updates = {
            planPPaymentsAge,
            formOfPayment,
            hoursWorked,
            payRate,
        }
        //console.log(`697 updates: ${JSON.stringify(updates)}`)
        const newLocal697 = _.defaults({}, updates, profile.pension.local697)

        if (
            !this.initCalc ||
            !_.isEqual(profile.pension.local697, newLocal697)
        ) {
            this.initCalc = true
            profile.pension.local697 = newLocal697
            this.debounceCalculations()
        }
    }

    updateHoursWorked(hoursWorked: number) {
        this.state.profile.pension.shared.hoursWorked = hoursWorked
        const exhibitGeneration = ExhibitGenerationState.idle
        this.setStateAndSave({ hoursWorked, exhibitGeneration })
    }

    updatePaymentsAge(planPPaymentsAge: moment.Duration) {
        this.state.profile.pension.local697.planPPaymentsAge = planPPaymentsAge
        const exhibitGeneration = ExhibitGenerationState.idle
        this.setState({ planPPaymentsAge, exhibitGeneration })

        this.markSignificantProfileUpdate(this.state.profile)
    }

    renderExhibitGeneration() {
        switch (this.state.exhibitGeneration) {
            case ExhibitGenerationState.idle: {
                return (
                    <div>
                        For information on benefits at other projected ages,
                        click&nbsp;
                        <a
                            onClick={() => {
                                this.onExportExhibits()
                            }}
                        >
                            here
                        </a>{" "}
                        to prepare the analysis.
                    </div>
                )
            }
            case ExhibitGenerationState.generating: {
                return (
                    <div>Please wait while your report is being generated.</div>
                )
            }
            case ExhibitGenerationState.ready:
            default: {
                return (
                    <div>
                        Your report is ready for download. Click to{" "}
                        <a href={this.state.downloadFilename}>here</a> to
                        download it.
                    </div>
                )
            }
        }
    }

    getRenderInfo() {
        // benefits
        const benefitAllowedPre65 = utils.toCurrency(
            Math.abs(this.state.info.benefitAllowedPre65)
        )
        const benefitAllowedPost65 = utils.toCurrency(
            Math.abs(this.state.info.benefitAllowedPost65)
        )

        // premiums
        const mhpPre65 = utils.toCurrency(this.state.info.mhpPre65)
        const mhpPost65 = utils.toCurrency(this.state.info.mhpPost65)

        // net premiums
        const a = this.state.info.mhpPre65 + this.state.info.benefitAllowedPre65
        const netPremiumPre65 = utils.toCurrency(a >= 0 ? a : 0)
        const b =
            this.state.info.mhpPost65 + this.state.info.benefitAllowedPost65
        const netPremiumPost65 = utils.toCurrency(b >= 0 ? b : 0)

        const planPPaymentsAgeText = utils.toNiceAge(
            this.state.planPPaymentsAge
        )

        return {
            benefitAllowedPre65,
            benefitAllowedPost65,
            mhpPre65,
            mhpPost65,
            netPremiumPre65,
            netPremiumPost65,
            planPPaymentsAgeText,
        }
    }

    renderIntro() {
        if (this.state.info && this.isPlanPEligible) {
            const info = this.getRenderInfo()
            return (
                <span>
                    Based on Plan records, you are eligible to receive a Retiree
                    Self-Payment Credit Benefit allowance. Upon your retirement,
                    this allowance will be automatically applied toward the
                    Health and Benefit (H&amp;B) Plan’s monthly premium. Note
                    that from age 55 through 64, the monthly H&amp;B premium is{" "}
                    {info.mhpPre65}. Upon attaining age 65, the monthly H&amp;B
                    premium is reduced to $550.
                </span>
            )
        }
        return (
            <span>
                Based on our records, you are not eligible for the Plan P
                benefit. If you think this is in error, please contact us{" "}
                <a href="#/support">here</a>.
            </span>
        )
    }

    renderRightSide() {
        const under65 = this.state.planPPaymentsAge.asYears() < 65
        const info = this.getRenderInfo()
        const details = new Array<JSX.Element>()
        const firstNamePossessive = utils.personPossessive(
            this.state.headOfHousehold.firstName
        )
        //details.push(<span>{JSON.stringify(info)}</span>)
        const expenseStyle = {
            color: "red",
        }

        let age1ToShow = info.planPPaymentsAgeText
        if (under65) {
            age1ToShow = "65"
            details.push(
                <span>
                    {firstNamePossessive} Retiree Self-Payment Credit Benefit
                    allowance at age <a>{info.planPPaymentsAgeText}</a> is{" "}
                    <a>{info.benefitAllowedPre65}</a>. The net H&amp;B monthly
                    premium payment would be{" "}
                    <a style={expenseStyle}>{info.netPremiumPre65}</a> (
                    {info.mhpPre65} - {info.benefitAllowedPre65}).&nbsp;
                </span>
            )
            details.push(<br />)
            details.push(<br />)
        }

        // this is fine- according to Paul
        details.push(
            <span>
                {firstNamePossessive} Retiree Self-Payment Credit Benefit
                allowance at age <a>{age1ToShow}</a> is{" "}
                <a>{info.benefitAllowedPost65}</a>. The net H&amp;B monthly
                premium payment would be{" "}
                <a style={expenseStyle}>{info.netPremiumPost65}</a> (
                {info.mhpPost65} - {info.benefitAllowedPost65}).
            </span>
        )

        return <h3 className="animated fadeIn">{details.map((x) => x)}</h3>
    }

    render() {
        if (!this.state.headOfHousehold) {
            return null
        }
        if (
            !this.state.profile.pension ||
            !this.state.profile.pension.local697 ||
            !this.state.planPPaymentsAge
        ) {
            return null
        }

        const noInfo = undefined === this.state.info || null === this.state.info
        const ppaPost65 = noInfo ? 0 : this.state.info.ppaPost65
        const ppaPre65 = noInfo ? 0 : this.state.info.ppaPre65
        const showSummary = ppaPre65 !== 0 || ppaPost65 !== 0
        // tslint:disable-next-line:no-console
        //console.log(`planP.render- showSummary: ${showSummary}, monthlyPayment: ${this.state.monthlyPayout}, ppaPre65: ${ppaPre65}, ppaPost65: ${ppaPost65}, noInfo: ${noInfo}`)

        const aStyle = {
            marginBottom: "10px",
        }

        return (
            <section className="animated fadeIn">
                <h1>Retiree Self-Payment Credit Benefit</h1>
                <h5 className="lead" style={aStyle}>
                    {this.renderIntro()}
                </h5>
                <br />

                {this.isPlanPEligible() && (
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <RetirementAgeSelector
                                    min={55}
                                    max={70}
                                    placeholder="What age will you begin payments?"
                                    selectedValue={this.state.planPPaymentsAge}
                                    onChange={(a) => this.updatePaymentsAge(a)}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            {showSummary && this.renderRightSide()}
                        </Col>
                    </Row>
                )}

                {this.isPlanPEligible() && (
                    <Row>
                        <Col md={6}>{this.renderExhibitGeneration()}</Col>
                    </Row>
                )}
            </section>
        )
    }
}

const Local697PlanPPensionQuestionsGroup = () => (
    <Local697PlanPPensionContainer
        header="Retiree Self-Payment Credit Benefit"
        icon=""
    />
)

export { Local697PlanPPensionQuestionsGroup }



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/pension/Local697PlanP/index.tsx